<template>
  <tr class="ra-question-row">
    <td
      v-if="categoryLabel !== null"
      class="ra-question-row__category"
      :rowspan="categoryRowSpan"
    >
      <div class="ra-question-row__category-label">
        {{ categoryLabel }}
      </div>
    </td>
    <td
      class="ra-question-row__question"
      :class="{ 'ra-question-row__question--unanswered': isUnanswered }"
    >
      {{ question.questionNo }}. {{ question.text }}
    </td>
    <td
      class="ra-question-row__cell ra-question-row__cell--applicable"
      :class="{ 'ra-question-row__cell--selected': isApplicable }"
    >
      <template v-if="question.multiSelect">
        <div class="ra-question-row__applicable-label">
          {{ question.applicableLabel }}
        </div>
        <template
          v-for="detail in question.details"
          :key="detail.key"
        >
          <!-- 入れ子サブ項目（妊娠Ver Q17）は親をラベル表示のみとし、葉だけを detail_key として選択させる。 -->
          <template v-if="detail.children.length > 0">
            <div class="ra-question-row__detail-group">
              {{ detail.label }}
            </div>
            <v-checkbox
              v-for="child in detail.children"
              :key="child.key"
              class="ra-question-row__detail-checkbox ra-question-row__detail-child"
              :model-value="isChecked(child.key)"
              :label="child.label"
              :disabled="disabled"
              color="black"
              density="compact"
              hide-details
              @update:model-value="emit('toggle-detail', child.key)"
            />
          </template>
          <v-checkbox
            v-else
            class="ra-question-row__detail-checkbox"
            :model-value="isChecked(detail.key)"
            :label="detail.label"
            :disabled="disabled"
            color="black"
            density="compact"
            hide-details
            @update:model-value="emit('toggle-detail', detail.key)"
          />
        </template>
      </template>
      <!-- 該当 / 非該当 / 不明 はセルをまたぐため v-radio-group で包めず、name を揃えてネイティブのラジオグループを成立させる。
           v-radio-group 外の v-radio は update:model-value を emit しない。また change は「選択済みの選択肢を
           もう一度押した」ときに発火しないため、未回答へ戻せるよう click で拾う（切替判定は呼び出し先が行う）。 -->
      <v-radio
        v-else
        :name="radioGroupName"
        :model-value="answerType"
        :value="ANSWER_TYPES.applicable"
        :label="question.applicableLabel"
        :disabled="disabled"
        color="black"
        hide-details
        @click="emit('select-answer-type', ANSWER_TYPES.applicable)"
      />
    </td>
    <td
      class="ra-question-row__cell ra-question-row__cell--choice"
      :class="{ 'ra-question-row__cell--selected': isNotApplicable }"
    >
      <v-radio
        :name="radioGroupName"
        :model-value="answerType"
        :value="ANSWER_TYPES.notApplicable"
        label="非該当"
        :disabled="disabled"
        color="black"
        hide-details
        @click="emit('select-answer-type', ANSWER_TYPES.notApplicable)"
      />
    </td>
    <td
      class="ra-question-row__cell ra-question-row__cell--choice"
      :class="{ 'ra-question-row__cell--selected': isUnknown }"
    >
      <v-radio
        :name="radioGroupName"
        :model-value="answerType"
        :value="ANSWER_TYPES.unknown"
        label="不明"
        :disabled="disabled"
        color="black"
        hide-details
        @click="emit('select-answer-type', ANSWER_TYPES.unknown)"
      />
    </td>
    <RiskAssessmentPreviousCell
      :question="question"
      :answer="previousAnswer"
    />
  </tr>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  ANSWER_TYPES,
  type RiskAssessmentAnswer,
  type RiskAssessmentAnswerType,
  type RiskAssessmentQuestion
} from "@/vue/entities/risk-assessment";
import type { AnswerDraft } from "../model/types";
import RiskAssessmentPreviousCell from "./RiskAssessmentPreviousCell.vue";

const props = withDefaults(
  defineProps<{
    question: RiskAssessmentQuestion;
    draft: AnswerDraft | null;
    previousAnswer: RiskAssessmentAnswer | null;
    categoryLabel?: string | null;
    categoryRowSpan?: number;
    disabled?: boolean;
  }>(),
  {
    categoryLabel: null,
    categoryRowSpan: 1,
    disabled: false
  }
);

const emit = defineEmits<{
  "select-answer-type": [RiskAssessmentAnswerType];
  "toggle-detail": [string];
}>();

const answerType = computed<RiskAssessmentAnswerType | null>(() => props.draft?.answerType ?? null);

const radioGroupName = computed<string>(() => `ra-question-${props.question.questionNo}`);

const isApplicable = computed<boolean>(() => answerType.value === ANSWER_TYPES.applicable);
const isNotApplicable = computed<boolean>(() => answerType.value === ANSWER_TYPES.notApplicable);
const isUnknown = computed<boolean>(() => answerType.value === ANSWER_TYPES.unknown);
// 未回答はエラーではなく未完了のため、aria-invalid ではなくマーカーで可視化する（DR-005）。
const isUnanswered = computed<boolean>(() => answerType.value === null);

const isChecked = (detailKey: string): boolean => props.draft?.detailKeys.includes(detailKey) ?? false;
</script>

<style lang="scss" scoped>
.ra-question-row td {
  border: 1px solid #d9d9d9;
  vertical-align: middle;
}

.ra-question-row__category {
  background-color: #f5f5f5;
  padding: 0;
}

.ra-question-row__category-label {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: center;
  font-size: 13px;
  white-space: pre-line;
  padding: 8px;
}

.ra-question-row__question {
  background-color: #fdeff2;
  font-size: 13px;
  padding: 12px;
}

.ra-question-row__question--unanswered {
  border-left: 4px solid #ef9a3d;
}

.ra-question-row__cell {
  padding: 12px;
  font-size: 13px;
}

.ra-question-row__cell--choice {
  text-align: center;
}

// 「非該当」がセル幅に収まらず2行に折り返すため、折り返しを禁止する。
.ra-question-row__cell--choice :deep(.v-label) {
  white-space: nowrap;
}

.ra-question-row__cell--selected {
  background-color: #dff3f0;
}

.ra-question-row__applicable-label {
  font-weight: bold;
  margin-bottom: 4px;
}

.ra-question-row__detail-group {
  font-size: 12px;
  margin-top: 4px;
}

.ra-question-row__detail-child {
  margin-left: 16px;
}

.ra-question-row__detail-checkbox :deep(.v-label) {
  font-size: 14px !important;
}
</style>
